import { createContext, useContext } from "react";

import type { WorkspaceTab } from "@/modules/workspace/workspaceTabs";

export type WorkspaceTabsContextValue = {
  tabs: WorkspaceTab[];
  activeId: string;
  dirtyIds: Set<string>;
  /** False when tabs are turned off in the profile. */
  enabled: boolean;
  canReopen: boolean;
  /** Open an address as a kept tab next to the one you are on. */
  openInNewTab: (destination: { pathname: string; search: string }) => void;
  selectTab: (id: string) => void;
  closeTab: (id: string) => void;
  closeOtherTabs: (id: string) => void;
  closeTabsToRight: (id: string) => void;
  /** Keep a preview tab so the next record opens beside it. */
  pinTab: (id: string) => void;
  duplicateTab: (id: string) => void;
  /** Drag reorder: put `fromId` where `toId` is. */
  moveTab: (fromId: string, toId: string) => void;
  reopenClosedTab: () => void;
  rememberTitle: (title: string) => void;
};

export const WorkspaceTabsContext =
  createContext<WorkspaceTabsContextValue | null>(null);

export const useWorkspaceTabs = () => {
  const value = useContext(WorkspaceTabsContext);
  if (!value) {
    throw new Error(
      "useWorkspaceTabs must be used inside WorkspaceTabsProvider",
    );
  }
  return value;
};

export const useWorkspaceTabsOptional = () => useContext(WorkspaceTabsContext);

/** True when the tab bar has something to show (two or more tabs, tabs on). */
export const useWorkspaceTabBarVisible = () => {
  const workspace = useContext(WorkspaceTabsContext);
  return Boolean(workspace?.enabled && workspace.tabs.length > 1);
};
